import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/comments`, {
        withCredentials: true
      });
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/comments/${id}/approve`, {}, {
        withCredentials: true
      });
      setComments(comments.map(c => c.id === id ? { ...c, approved: true } : c));
    } catch (error) {
      console.error('Error approving comment:', error);
      alert('Failed to approve comment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/comments/${id}`, {
        withCredentials: true
      });
      setComments(comments.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  if (loading) {
    return <div data-testid="loading-state">Loading...</div>;
  }

  return (
    <div data-testid="admin-comments-page">
      <h1 className="font-serif text-4xl font-bold mb-8">Comments</h1>

      <div className="bg-card border border-border">
        <table className="w-full">
          <thead className="border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold">Author</th>
              <th className="text-left p-4 font-semibold">Email</th>
              <th className="text-left p-4 font-semibold">Comment</th>
              <th className="text-left p-4 font-semibold">Status</th>
              <th className="text-left p-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {comments.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-muted-foreground">
                  No comments yet
                </td>
              </tr>
            ) : (
              comments.map((comment) => (
                <tr key={comment.id} data-testid={`comment-row-${comment.id}`} className="border-b border-border hover:bg-muted">
                  <td className="p-4">{comment.author_name}</td>
                  <td className="p-4">{comment.author_email}</td>
                  <td className="p-4 max-w-md truncate">{comment.content}</td>
                  <td className="p-4">
                    <span className={`px-3 py-1 text-xs rounded-sm font-medium ${
                      comment.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {comment.approved ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      {!comment.approved && (
                        <button
                          onClick={() => handleApprove(comment.id)}
                          data-testid={`approve-comment-${comment.id}`}
                          className="text-green-600 hover:text-green-700"
                          title="Approve"
                        >
                          <Check size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(comment.id)}
                        data-testid={`delete-comment-${comment.id}`}
                        className="text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminComments;